import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import cx from 'classnames';

export interface ActionTileProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: ReactNode;
  children: ReactNode;
  /** `primary` is the main entry of a column; `outline` is an extra one next to it. */
  variant?: 'primary' | 'outline';
  /** `lg` is the square tile (128px); `md` is the same tile at its content height. */
  size?: 'md' | 'lg';
}

const variantClasses = {
  primary:
    'border-primary bg-primary text-white hover:border-primary-hover hover:bg-primary-hover active:border-primary-active active:bg-primary-active',
  outline: 'border-primary bg-surface-muted text-ink hover:bg-danger-surface active:bg-primary/12',
};

/** A big button with the icon above its caption — the entry points in a side column. */
export const ActionTile = forwardRef<HTMLButtonElement, ActionTileProps>(
  ({ icon, children, variant = 'primary', size = 'lg', type = 'button', className, ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cx(
        'flex w-full flex-col items-center justify-center gap-1.5 rounded-card border-[1.5px] px-4 text-center text-label leading-tight transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:border-neutral disabled:bg-neutral disabled:text-ink-subtle',
        size === 'lg' ? 'min-h-32 py-4' : 'py-3',
        variantClasses[variant],
        className,
      )}
      {...rest}
    >
      {icon}
      <span>{children}</span>
    </button>
  ),
);

ActionTile.displayName = 'ActionTile';
